// features/projects/templates/professional/widgets/pulse-hero.js
// ═══════════════════════════════════════════════════════════
// PULSE HERO — auto-synthesized weekly briefing for the
// dashboard's top slot.
//
// Picks the load-bearing finding of the week and frames it as an
// editorial headline + supporting paragraph + stat strip. Reads
// like "this week's wealth pulse" but for research.
//
// Not user-removable. Always anchors the dashboard.
// ═══════════════════════════════════════════════════════════

import { useMemo } from 'react';
import { L, FONT } from '../tokens';
import { useWidgetData } from './data-provider';
import { DYNAMICS } from '../../../../../lib/continuum/simulation/methodology';

const DYN_BY_KEY = Object.fromEntries(DYNAMICS.map(d => [d.key, d]));

export default function PulseHero({ projectName }) {
  const data = useWidgetData();
  const synthesis = useMemo(() => composePulse(data), [data]);

  return (
    <div style={{
      position: 'relative',
      padding: '28px 32px',
      background: L.s2,
      border: `1px solid ${L.b1}`,
      borderRadius: 4,
      boxShadow: `inset 0 -2px 0 0 ${L.brass}30`,
      marginBottom: 18,
    }}>
      <CornerTicks color={L.brass} opacity={0.5} />

      {/* Tag */}
      <div style={{
        fontFamily: FONT.mono, fontSize: 10, letterSpacing: '0.22em',
        color: L.brass, textTransform: 'uppercase', fontWeight: 600,
        marginBottom: 14,
      }}>
        ◆ This week · {projectName || 'Observatory'}
      </div>

      {/* Headline */}
      <div style={{
        fontFamily: FONT.serif, fontSize: 28, fontWeight: 400,
        color: L.moonglow, lineHeight: 1.25,
        marginBottom: 14, maxWidth: 880,
        letterSpacing: '-0.005em',
      }}>
        {synthesis.headline}
      </div>

      {/* Supporting paragraph */}
      {synthesis.body && (
        <div style={{
          fontFamily: FONT.sans, fontSize: 14, color: L.silverOxide,
          lineHeight: 1.65, marginBottom: 22, maxWidth: 760,
        }}>
          {synthesis.body}
        </div>
      )}

      {/* Stat strip */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 18,
        fontFamily: FONT.mono, fontSize: 10, color: L.dim,
        letterSpacing: '0.14em', textTransform: 'uppercase',
      }}>
        {synthesis.stats.map((s, i) => (
          <span key={i} style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
          }}>
            {i > 0 && <span style={{ color: L.b3 }}>·</span>}
            <span style={{ color: L.text, fontWeight: 600 }}>{s.value}</span>
            <span>{s.label}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function composePulse({ comparisons = [], hypotheses = [], predictions = [], questions = [] }) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const substantive = comparisons.filter(c =>
    c.verdict && !['pending', 'insufficient_data', 'error'].includes(String(c.verdict).toLowerCase())
  );
  const survived = substantive.filter(c => c.phase_a_status === 'survived' || c.phase_a_status === 'survived-respecified');
  const respec = substantive.filter(c => c.phase_a_status === 'survived-respecified');
  const due = predictions.filter(p =>
    p.status === 'pending' && new Date(p.resolution_date) <= today
  );
  const pending = comparisons.filter(c =>
    !c.verdict || ['pending', 'insufficient_data'].includes(String(c.verdict).toLowerCase())
  );

  // Pick the headline anchor: most recently Phase-A'd > strongest
  // substantive > newest hypothesis > newest finding overall.
  const recentPhaseA = substantive
    .filter(c => c.phase_a_status && c.phase_a_run_at)
    .slice().sort((a, b) => new Date(b.phase_a_run_at) - new Date(a.phase_a_run_at))[0];
  const strongest = substantive.slice().sort((a, b) => threadScore(b) - threadScore(a))[0];
  const newestHyp = hypotheses.slice().sort((a, b) =>
    new Date(b.registered_at || 0) - new Date(a.registered_at || 0))[0];

  // Build headline
  let headline, body;
  if (recentPhaseA) {
    const dyn = DYN_BY_KEY[recentPhaseA.dynamic_key];
    const dynLabel = dyn?.label || recentPhaseA.dynamic_key;
    if (recentPhaseA.phase_a_status === 'survived-respecified') {
      headline = `${dynLabel} survives Phase A in respecified form — load-bearing claim narrowed, methodology working as designed.`;
    } else if (recentPhaseA.phase_a_status === 'survived') {
      headline = `${dynLabel} survived Phase A intact. Defensible to cite externally with the standard sample-size caveats.`;
    } else if (recentPhaseA.phase_a_status === 'killed') {
      headline = `${dynLabel} killed by Phase A. Treat as evidence against, not as a null result.`;
    }
  } else if (strongest) {
    const dyn = DYN_BY_KEY[strongest.dynamic_key];
    const dynLabel = dyn?.label || strongest.dynamic_key;
    headline = `${dynLabel} leads the substantive findings — d=${(Number(strongest.effect_size) || 0).toFixed(2)}, ${(strongest.confidence || 'unknown').toLowerCase()} confidence. Phase A ${strongest.phase_a_status ? 'complete' : 'pending'}.`;
  } else if (pending.length > 0) {
    headline = `${pending.length} ${pending.length === 1 ? 'dynamic' : 'dynamics'} pending data. Findings begin once both sides have observations.`;
  } else {
    headline = `Workspace is set up but no data yet. Wire sources, run a simulation, and findings will appear here.`;
  }

  // Build body — context anchored on the headline state
  const bodyParts = [];
  if (due.length > 0) {
    bodyParts.push(`${due.length} prediction${due.length === 1 ? ' has' : 's have'} reached its resolution date.`);
  }
  if (pending.length > 0) {
    bodyParts.push(`${pending.length} ${pending.length === 1 ? 'dynamic remains' : 'dynamics remain'} pending data.`);
  }
  if (respec.length > 0 && substantive.length > 0) {
    bodyParts.push(`${respec.length} of ${substantive.length} substantive ${respec.length === 1 ? 'finding has' : 'findings have'} been respecified through Phase A — the methodology is producing precision, not invalidation.`);
  }
  if (newestHyp && hypotheses.length > 0) {
    const dyn = DYN_BY_KEY[newestHyp.dynamic_key];
    const dynLabel = dyn?.label || newestHyp.dynamic_key;
    bodyParts.push(`Latest hypothesis registered: ${dynLabel}.`);
  }
  body = bodyParts.length > 0 ? bodyParts.join(' ') : null;

  // Stat strip
  const stats = [
    { value: comparisons.length || 0, label: 'findings tracked' },
    { value: hypotheses.length || 0, label: 'hypotheses registered' },
    { value: predictions.filter(p => p.status === 'pending').length, label: 'predictions pending' },
  ];
  if (questions.length > 0) {
    stats.push({ value: questions.length, label: 'open questions' });
  }

  return { headline, body, stats };
}

function threadScore(c) {
  let s = 0;
  if (c.phase_a_status === 'survived') s += 100;
  else if (c.phase_a_status === 'survived-respecified') s += 80;
  if ((c.confidence || '').toUpperCase() === 'HIGH') s += 50;
  return s;
}

function CornerTicks({ color = L.brass, opacity = 0.5 }) {
  const t = {
    position: 'absolute', width: 8, height: 8,
    borderColor: color, borderStyle: 'solid', opacity,
    pointerEvents: 'none',
  };
  return (
    <>
      <span style={{ ...t, top: 6, left: 6, borderWidth: '1px 0 0 1px' }} />
      <span style={{ ...t, top: 6, right: 6, borderWidth: '1px 1px 0 0' }} />
      <span style={{ ...t, bottom: 6, left: 6, borderWidth: '0 0 1px 1px' }} />
      <span style={{ ...t, bottom: 6, right: 6, borderWidth: '0 1px 1px 0' }} />
    </>
  );
}
